import DataStructure, { sanitize } from "./DataStructure";
import { store } from "../store";
import { i18n } from "../boot/i18n";
import { router } from "../router";
import { DialogRouter } from "../boot/dialogs";
import { deepFreeze } from "../util/misc";
import { COLORS } from "../constants";
import { compact, sortBy } from "lodash";
import { computeSaleStatus } from "../util/saleStatus.js";

// Delivery/pickup progress numerators (delivery.complete / scheduled, etc.)
// are counted via `classifySaleItem`, which treats accessories and numbered
// hardware as deliverable "units". The denominators come from the
// quote-derived `total.items`, which classifies those same items
// differently — so the numerator can exceed `total.items` and produce a
// nonsensical ">100%" ratio (e.g. sale 04100003A showed delivery 32 / total
// 31 because a $0 accessory on the line-20 window was delivered but never
// added to `total.items`). Progress can never exceed 100%, so floor the
// denominator at the numerator. When numerator <= denominator this is a
// no-op, so normal in-progress ratios are unaffected.
function clampProgress(numerator, denominator, extra = {}) {
  const clamped = Math.max(denominator, numerator);
  return {
    percent: clamped ? (100 * numerator) / clamped : 0,
    numerator,
    denominator: clamped,
    ...extra,
  };
}

export const SALE_STATUS_FIELDS = {
  type: "ordering",
  vendors: {},
  orders: {},
  changeOrders: {},
  acks: {},
  bills: {},
  packingSlips: {},
  issues: {},
  delivery: {
    ready: 0,
    scheduled: 0,
    complete: 0,
    screensReady: 0,
    screensScheduled: 0,
    screensComplete: 0,
    hardwareReady: 0,
    hardwareScheduled: 0,
    hardwareComplete: 0,
    // Child accessories (items with a `parentID`). They are physically
    // separate stock — separately received, QC'd, and loaded — so they get
    // their own counter triple rather than being folded into `ready` /
    // `scheduled` / `complete`, whose denominator (`total.items`) is
    // quote-line based and excludes them.
    accessoriesReady: 0,
    accessoriesScheduled: 0,
    accessoriesComplete: 0,
    needsReschedule: 0,
    events: [],
  },
  pickup: {
    ready: 0,
    scheduled: 0,
    complete: 0,
    screensScheduled: 0,
    screensComplete: 0,
    hardwareScheduled: 0,
    hardwareComplete: 0,
    accessoriesReady: 0,
    accessoriesScheduled: 0,
    accessoriesComplete: 0,
    events: [],
  },
  install: {
    totalHours: 0,
    totalItems: 0,
    totalLabors: 0,
    readyItems: 0,
    scheduledHours: 0,
    completeHours: 0,
    completeItems: 0,
    completeLabors: 0,
    needsReschedule: 0,
    events: [],
  },
  invoices: {
    // Invoice ID: { events, paid, total }
  },
  service: {
    // Service issue rollup, written ONLY by functions/service.js
    // onServiceIssueWrite (a sale in service is usually locked, and the rules
    // refuse client writes to a locked sale). The counters partition the
    // total: resolved + scheduled (open, on a work order) + unscheduled — a
    // resolved issue keeps its workOrderID but is not counted as scheduled.
    issues: {}, // { issueID: { isResolved, isCritical, workOrderID } }
    totalIssues: 0,
    resolvedIssues: 0,
    scheduledIssues: 0,
  },
  downPaymentRequested: false,
  punchOutComplete: false,
  // One-shot guard: set true once accounting has been alerted to collect the
  // material balance on an unpaid cash sale (>=75% of material received).
  materialCollectionNotified: false,
};
deepFreeze(SALE_STATUS_FIELDS);

function editSalesLead() {
  DialogRouter.push({
    name: "sales-lead-edit",
    params: {
      salesLeadID: this._parent.data.leadID,
    },
  });
}

function requestDownPayment() {
  store.dispatch("PROMPT", {
    title: i18n.global.t("Confirm"),
    message: i18n.global.t("confirm.down-payment-requested"),
    success: () =>
      store.dispatch("UPDATE_SALE_STATUS", {
        projectID: this._parent.projectID,
        saleID: this._parent.id,
        changes: { "status.downPaymentRequested": true },
      }),
  });
}

function enterDownPayment() {
  DialogRouter.push({
    name: "tx-new",
    params: {
      type: "deferred-down-payment",
      forID: this._parent.stateID,
    },
  });
}

// The section rides on the action (`actionSection`), so the project card can
// open its own tab of the same name instead of leaving for the sale page
function goToSection(section) {
  return Object.assign(
    function () {
      router.replace({
        name: "sale-view",
        params: {
          projectID: this._parent.projectID,
          saleID: this._parent.id,
          section,
        },
      });
    },
    { section }
  );
}

function pickUp() {
  DialogRouter.push({
    name: "event-type-new",
    params: {
      type: "pickup",
      item: this._parent.stateID,
    },
  });
}

function deliverOrPickUp() {
  // Check if the next pending delivery event is actually a pickup
  const events = compact(
    (this.data.delivery?.events || []).map((id) => store.state.main.events[id])
  );
  const pickupEvent = events.find(
    (e) => e.data.type === "pickup" && e.status?.type !== "pickedUp"
  );
  if (pickupEvent) {
    router.push({
      name: "pickup",
      params: {
        projectID: this._parent.projectID,
        saleID: this._parent.id,
        pickupID: pickupEvent.id,
      },
    });
    return;
  }
  router.replace({
    name: "sale-view",
    params: {
      projectID: this._parent.projectID,
      saleID: this._parent.id,
      section: "events",
    },
  });
}

export const INSTALL_READINESS_TYPES = deepFreeze({
  delivered: {
    order: 0,
    label: "Delivered, not installed",
    icon: "delivered",
    color: "positive",
  },
  received: {
    order: 1,
    label: "Received, not installed",
    icon: "packing_slip",
    color: "positive",
  },
  inProduction: {
    order: 2,
    label: "Acknowledged, awaiting receipt",
    icon: "production",
    color: "warning",
  },
  awaitingAck: {
    order: 3,
    label: "Ordered, awaiting ack",
    icon: "ack",
    color: "warning",
  },
  notOrdered: {
    order: 4,
    label: "Not ordered",
    icon: "order",
    color: "negative",
  },
});

export const SALE_STATUS_TYPES = {
  changeOrder: {
    order: -1,
    label: "Pending change order",
    icon: "change_order",
    color: "warning",
    action: editSalesLead,
  },
  downPayment: {
    order: 0,
    label: "Awaiting down payment",
    icon: "awaiting_payment",
    color: "negative",
    action: requestDownPayment,
  },
  downPaymentRequested: {
    order: 1,
    label: "Down payment requested",
    icon: "awaiting_payment",
    color: "warning",
    action: enterDownPayment,
  },
  ordering: {
    order: 2,
    label: "Ready to order",
    icon: "order",
    color: "warning",
    action: goToSection("vendor"),
  },
  ack: {
    order: 3,
    label: "Awaiting acknowledgement",
    icon: "ack",
    color: "warning",
    action: goToSection("vendor"),
  },
  production: {
    order: 4,
    label: "In production",
    icon: "vendor",
    color: "positive",
    action: goToSection("vendor"),
  },
  qc: {
    order: 5,
    label: "Quality Control",
    icon: "quality_control",
    color: "warning",
    action: goToSection("vendor"),
  },
  issuesCritical: {
    order: 6,
    label: "Critical sale issues",
    icon: "issue",
    color: "negative",
    action: goToSection("issues"),
  },
  scheduling: {
    order: 8,
    label: "Scheduling",
    icon: "scheduling",
    color: "warning",
    action: goToSection("events"),
  },
  scheduled: {
    order: 9,
    label: "Ready to deliver",
    icon: "delivery",
    color: "warning",
    action: deliverOrPickUp,
  },
  pickup: {
    order: 10,
    label: "Ready for pickup",
    icon: "pickup",
    color: "warning",
    action: pickUp,
  },
  install: {
    order: 11,
    label: "Ready to install",
    icon: "installation",
    color: "warning",
    action: goToSection("labor"),
  },
  installing: {
    order: 12,
    label: "Installing",
    icon: "pending",
    color: "warning",
    action: goToSection("labor"),
  },
  billing: {
    order: 13,
    label: "Awaiting bill",
    icon: "bill",
    color: "warning",
    action: goToSection("vendor"),
  },
  paying: {
    order: 14,
    label: "Ready to pay",
    icon: "paying",
    color: "warning",
    action: goToSection("accounting"),
  },
  collecting: {
    order: 15,
    label: "Awaiting payment",
    icon: "awaiting_payment",
    color: "warning",
    action: goToSection("accounting"),
  },
  service: {
    order: 16,
    label: "Service",
    icon: "service",
    color: "positive",
  },
  issues: {
    order: 17,
    label: "Unresolved sale issues",
    icon: "issue",
    color: "warning",
    action: goToSection("issues"),
  },
  complete: {
    order: 18,
    label: "Complete",
    icon: "done",
    color: "positive",
  },
  canceled: {
    order: 19,
    label: "Canceled",
    icon: "cancel",
    color: "negative",
  },
};
deepFreeze(SALE_STATUS_TYPES);

// A status type key (a sale's `status.type`, or the project's mirror of it)
// as its label, icon and colours (`bg`/`fg`), or null for an unknown key.
export function saleStatusType(key) {
  const type = SALE_STATUS_TYPES[key];
  return type ? { ...type, ...COLORS[type.color] } : null;
}

export default class SaleStatus extends DataStructure {
  constructor(parent, data = {}) {
    // Backward compatibility
    if (data.current && !data.type) {
      data.type = data.current;
    }

    super(sanitize(data, SALE_STATUS_FIELDS));
    this._parent = parent;
  }

  get hasUnresolvedIssue() {
    return Object.values(this.data.issues).some((issue) => !issue.isResolved);
  }

  get hasUnresolvedCriticalIssue() {
    return Object.values(this.data.issues).some(
      (issue) => !issue.isResolved && issue.isCritical
    );
  }

  get hasPendingChangeOrder() {
    const lead = this._parent.lead;
    if (this._parent.data.leadID && !lead) {
      // Fire-and-forget lazy load; this getter runs on every render, so a
      // dangling leadID would otherwise reject once per render.
      store
        .dispatch("GET_SALES_LEAD", this._parent.data.leadID)
        .catch(() => {});
    }
    return (
      (lead && lead.data.status.outcome.type === "pending-change-order") ||
      Object.values(this.data.changeOrders).includes("pending")
    );
  }

  get isAwaitingDownPayment() {
    return Boolean(
      !this._parent.downPaymentAmount &&
        this._parent.downPaymentDue &&
        this._parent.data.total.balance > 0
    );
  }

  get isFullyOrdered() {
    return Object.values(this.data.vendors).every(
      (vendor) => vendor.fullyOrdered
    );
  }

  get isFullyAcked() {
    return Object.values(this.data.orders).every((order) => order.fullyAcked);
  }

  get isFullyReceived() {
    return Object.values(this.data.acks).every((ack) => ack.fullyReceived);
  }

  get isQCInspected() {
    return Object.values(this.data.packingSlips).every((ps) => ps.qcInspected);
  }

  get isFullyScreenScheduled() {
    return (
      this.data.delivery.screensScheduled >= this._parent.data.total.screens
    );
  }

  get isFullyHardwareScheduled() {
    return (
      this.data.delivery.hardwareScheduled >= this._parent.data.total.hardware
    );
  }

  get isFullyAccessoriesScheduled() {
    const total = this._parent.data.total.accessories || 0;
    return (this.data.delivery.accessoriesScheduled || 0) >= total;
  }

  get isFullyAccessoriesDelivered() {
    const total = this._parent.data.total.accessories || 0;
    return (this.data.delivery.accessoriesComplete || 0) >= total;
  }

  get hasDeliveryNeedsReschedule() {
    return (this.data.delivery.needsReschedule || 0) > 0;
  }

  get hasInstallNeedsReschedule() {
    return (this.data.install.needsReschedule || 0) > 0;
  }

  get isFullyScheduled() {
    return (
      (this.data.delivery.scheduled >= this._parent.data.total.items ||
        this.data.install.readyItems >= this._parent.data.total.items) &&
      (this.isFullyInstalled ||
        this.data.install.scheduledHours >= this.data.install.totalHours) &&
      (this._parent.data.holdScreens || this.isFullyScreenScheduled) &&
      (this._parent.data.holdHardware || this.isFullyHardwareScheduled) &&
      this.isFullyAccessoriesScheduled &&
      !this.hasDeliveryNeedsReschedule &&
      !this.hasInstallNeedsReschedule
    );
  }

  get isFullyScreenDelivered() {
    return (
      this.data.delivery.screensComplete >= this._parent.data.total.screens
    );
  }

  get isFullyHardwareDelivered() {
    return (
      this.data.delivery.hardwareComplete >= this._parent.data.total.hardware
    );
  }

  get isFullyDelivered() {
    return (
      this.data.delivery.complete >= this._parent.data.total.items &&
      (this._parent.data.holdScreens || this.isFullyScreenDelivered) &&
      (this._parent.data.holdHardware || this.isFullyHardwareDelivered) &&
      this.isFullyAccessoriesDelivered
    );
  }

  get isPickupSale() {
    return Boolean(this._parent.data.shipTo?.pickup);
  }

  get pickupLocation() {
    if (!this.isPickupSale) return null;
    return this._parent.data.shipTo?.address || null;
  }

  get isFullyPickedUp() {
    if (!this.isPickupSale) return true;
    return (
      (this.data.pickup?.complete || 0) >= this._parent.data.total.items &&
      (this._parent.data.holdScreens ||
        (this.data.pickup?.screensComplete || 0) >=
          this._parent.data.total.screens) &&
      (this._parent.data.holdHardware ||
        (this.data.pickup?.hardwareComplete || 0) >=
          this._parent.data.total.hardware) &&
      (this.data.pickup?.accessoriesComplete || 0) >=
        (this._parent.data.total.accessories || 0)
    );
  }

  get isFullyInstalled() {
    return this.data.install.completeLabors >= this.data.install.totalLabors;
  }

  get isInstalling() {
    return (
      this.data.install.completeItems > 0 &&
      this.data.install.completeLabors < this.data.install.totalLabors
    );
  }

  get isFullyBilled() {
    return Object.values(this.data.acks).every((ack) => ack.fullyBilled);
  }

  get isFullyPaid() {
    return (
      this.isFullyBilled &&
      Object.values(this.data.bills).every((bill) => bill.fullyPaid)
    );
  }
  get isPunchOutComplete() {
    return (
      !this._parent.data.total.items ||
      !this._parent.data.requiresPunchOut ||
      this.data.punchOutComplete
    );
  }

  // Service Issue Tracking
  get hasOpenServiceIssues() {
    return (
      this.data.service.totalIssues > 0 &&
      this.data.service.resolvedIssues < this.data.service.totalIssues
    );
  }

  get hasUnscheduledServiceIssues() {
    const unscheduled =
      this.data.service.totalIssues -
      this.data.service.resolvedIssues -
      this.data.service.scheduledIssues;
    return unscheduled > 0;
  }

  get serviceIssueCount() {
    return this.data.service.totalIssues;
  }

  get openServiceIssueCount() {
    return this.data.service.totalIssues - this.data.service.resolvedIssues;
  }

  get() {
    // Use the shared computeSaleStatus function (single source of truth)
    // Pass computed properties as options since they require parent data
    return computeSaleStatus(
      {
        status: this.data,
        total: this._parent.data.total,
        terms: this._parent.data.terms,
        holdScreens: this._parent.data.holdScreens,
        holdHardware: this._parent.data.holdHardware,
        requiresPunchOut: this._parent.data.requiresPunchOut,
        downPaymentAmount: this._parent.data.downPaymentAmount,
        shipTo: this._parent.data.shipTo,
      },
      {
        hasPendingChangeOrder: this.hasPendingChangeOrder,
        isAwaitingDownPayment: this.isAwaitingDownPayment,
      }
    );
  }

  // Formatting

  get deliveryEvents() {
    return sortBy(
      compact(
        this.data.delivery.events.map((id) => store.state.main.events[id])
      ),
      ["start", "end"]
    );
  }

  get installationEvents() {
    return sortBy(
      compact(
        this.data.install.events.map((id) => store.state.main.events[id])
      ),
      ["start", "end"]
    );
  }

  get pickupEvents() {
    return sortBy(
      compact(
        (this.data.pickup?.events || []).map(
          (id) => store.state.main.events[id]
        )
      ),
      ["start", "end"]
    );
  }

  get progressDelivery() {
    const ready = this.percentDeliveryReady;
    const scheduled = this.percentDeliveryScheduled;
    if (ready.percent < 100) {
      return ready;
    } else if (scheduled.percent < 100) {
      return scheduled;
    } else {
      return this.percentDelivered;
    }
  }

  // Child accessories have no "hold" concept — they are always part of the
  // Full Units flow — so every progress row below folds them in unconditionally
  // whenever the sale has any. Keep this in step with `Sale.displayItemCount`
  // and `SaleItemProgress`, which count them the same way.
  get totalAccessories() {
    return this._parent.data.total.accessories || 0;
  }

  get percentReceived() {
    let numerator = this.data.delivery.ready;
    let denominator = this._parent.data.total.items;
    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      numerator += this._parent.data.total.screens;
      denominator += this._parent.data.total.screens;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      numerator += this._parent.data.total.hardware;
      denominator += this._parent.data.total.hardware;
    }
    if (this.totalAccessories) {
      numerator += this.data.delivery.accessoriesReady || 0;
      denominator += this.totalAccessories;
    }
    return clampProgress(numerator, denominator);
  }

  get percentDeliveryReady() {
    // `complete` counters can exceed the quote-derived totals (see the
    // `clampProgress` comment above), so each "remaining" term is floored at 0
    // to avoid negative numerator/denominator values surfacing in the
    // Deliveries to Schedule widget (e.g. a nonsensical "-1 / -6").
    let numerator = Math.max(
      0,
      this.data.delivery.ready - this.data.delivery.complete
    );
    let denominator = Math.max(
      0,
      this._parent.data.total.items - this.data.delivery.complete
    );
    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      const remaining = Math.max(
        0,
        this._parent.data.total.screens - this.data.delivery.screensComplete
      );
      numerator += remaining;
      denominator += remaining;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      const remaining = Math.max(
        0,
        this._parent.data.total.hardware - this.data.delivery.hardwareComplete
      );
      numerator += remaining;
      denominator += remaining;
    }
    if (this.totalAccessories) {
      const complete = this.data.delivery.accessoriesComplete || 0;
      numerator += Math.max(
        0,
        (this.data.delivery.accessoriesReady || 0) - complete
      );
      denominator += Math.max(0, this.totalAccessories - complete);
    }
    return clampProgress(numerator, denominator, { color: COLORS.positive });
  }

  get percentDeliveryScheduleReady() {
    let numerator = this.data.delivery.ready - this.data.delivery.scheduled;
    let denominator =
      this._parent.data.total.items - this.data.delivery.scheduled;

    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      numerator +=
        this._parent.data.total.screens - this.data.delivery.screensScheduled;
      denominator += this._parent.data.total.screens;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      numerator +=
        this._parent.data.total.hardware - this.data.delivery.hardwareScheduled;
      denominator += this._parent.data.total.hardware;
    }
    if (this.totalAccessories) {
      numerator +=
        (this.data.delivery.accessoriesReady || 0) -
        (this.data.delivery.accessoriesScheduled || 0);
      denominator += this.totalAccessories;
    }
    let percent = denominator ? (100 * numerator) / denominator : 0;
    return {
      percent: Math.max(0, percent),
      numerator: Math.max(0, numerator),
      denominator,
      color: COLORS.positive,
    };
  }

  get percentDeliveryScheduled() {
    let numerator = this.data.delivery.scheduled;
    let denominator = this._parent.data.total.items;
    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      numerator += this.data.delivery.screensScheduled;
      denominator += this._parent.data.total.screens;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      numerator += this.data.delivery.hardwareScheduled;
      denominator += this._parent.data.total.hardware;
    }
    if (this.totalAccessories) {
      numerator += this.data.delivery.accessoriesScheduled || 0;
      denominator += this.totalAccessories;
    }
    return clampProgress(numerator, denominator, { color: COLORS.positive });
  }

  get percentDelivered() {
    let numerator = this.data.delivery.complete;
    let denominator = this._parent.data.total.items;
    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      numerator += this.data.delivery.screensComplete;
      denominator += this._parent.data.total.screens;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      numerator += this.data.delivery.hardwareComplete;
      denominator += this._parent.data.total.hardware;
    }
    if (this.totalAccessories) {
      numerator += this.data.delivery.accessoriesComplete || 0;
      denominator += this.totalAccessories;
    }
    return clampProgress(numerator, denominator);
  }

  get percentPickupScheduleReady() {
    const pickup = this.data.pickup || {};
    // For pickup sales, `pickup.ready` is authoritative — it is kept in
    // sync with BOTH delivery-to-pickup-location events AND QC-direct
    // into the pickup warehouse (via UPDATE_SALE_ITEM_STATUS and its
    // backend mirrors). For non-pickup sales, warehouse stock counts as
    // "ready for pickup" so we read from `delivery.ready`.
    const useDeliveryReady = !this.isPickupSale;
    let numerator = useDeliveryReady
      ? this.data.delivery.ready
      : pickup.ready || 0;
    let denominator = this._parent.data.total.items;
    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      numerator += useDeliveryReady
        ? this.data.delivery.screensReady || 0
        : pickup.screensReady || 0;
      denominator += this._parent.data.total.screens;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      numerator += useDeliveryReady
        ? this.data.delivery.hardwareReady || 0
        : pickup.hardwareReady || 0;
      denominator += this._parent.data.total.hardware;
    }
    if (this.totalAccessories) {
      numerator += useDeliveryReady
        ? this.data.delivery.accessoriesReady || 0
        : pickup.accessoriesReady || 0;
      denominator += this.totalAccessories;
    }
    let percent = denominator ? (100 * numerator) / denominator : 0;
    return {
      percent: Math.max(0, percent),
      numerator: Math.max(0, numerator),
      denominator,
      color: COLORS.positive,
    };
  }

  get percentPickupScheduled() {
    const pickup = this.data.pickup || {};
    let numerator = pickup.scheduled || 0;
    let denominator = this._parent.data.total.items;
    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      numerator += pickup.screensScheduled || 0;
      denominator += this._parent.data.total.screens;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      numerator += pickup.hardwareScheduled || 0;
      denominator += this._parent.data.total.hardware;
    }
    if (this.totalAccessories) {
      numerator += pickup.accessoriesScheduled || 0;
      denominator += this.totalAccessories;
    }
    return clampProgress(numerator, denominator, { color: COLORS.positive });
  }

  get percentPickedUp() {
    const pickup = this.data.pickup || {};
    let numerator = pickup.complete || 0;
    let denominator = this._parent.data.total.items;
    if (!this._parent.data.holdScreens && this._parent.data.total.screens) {
      numerator += pickup.screensComplete || 0;
      denominator += this._parent.data.total.screens;
    }
    if (!this._parent.data.holdHardware && this._parent.data.total.hardware) {
      numerator += pickup.hardwareComplete || 0;
      denominator += this._parent.data.total.hardware;
    }
    if (this.totalAccessories) {
      numerator += pickup.accessoriesComplete || 0;
      denominator += this.totalAccessories;
    }
    return clampProgress(numerator, denominator);
  }

  get percentInstallItemsReady() {
    const numerator = this.data.install.readyItems || 0;
    const denominator = this.data.install.totalItems || 0;
    const percent = denominator > 0 ? (100 * numerator) / denominator : 0;
    return { percent, numerator, denominator };
  }

  get percentInstallItemsScheduled() {
    const numerator = this.installationEvents.reduce((sum, event) => {
      if (!event.items) return sum;
      return (
        sum +
        event.items.reduce((itemSum, item) => itemSum + (item.quantity || 1), 0)
      );
    }, 0);
    const denominator = this.data.install.totalItems || 0;
    const percent = denominator > 0 ? (100 * numerator) / denominator : 0;
    return { percent, numerator, denominator };
  }

  get percentInstallItemsComplete() {
    const numerator = this.data.install.completeItems || 0;
    const denominator = this.data.install.totalItems || 0;
    const percent = denominator > 0 ? (100 * numerator) / denominator : 0;
    return { percent, numerator, denominator };
  }

  get percentInstallHoursScheduled() {
    return {
      numerator: this.data.install.scheduledHours || 0,
      denominator: this.data.install.totalHours || 0,
    };
  }

  get percentInstallHoursComplete() {
    return {
      numerator: this.data.install.completeHours || 0,
      denominator: this.data.install.totalHours || 0,
    };
  }

  get percentHeldScheduled() {
    let numerator = 0;
    let denominator = 0;
    const total = this._parent.data.total;
    if (this._parent.data.holdScreens && total.screens > 0) {
      numerator += this.data.delivery.screensScheduled || 0;
      denominator += total.screens;
    }
    if (this._parent.data.holdHardware && total.hardware > 0) {
      numerator += this.data.delivery.hardwareScheduled || 0;
      denominator += total.hardware;
    }
    return { numerator, denominator };
  }

  get percentHeldDelivered() {
    let numerator = 0;
    let denominator = 0;
    const total = this._parent.data.total;
    if (this._parent.data.holdScreens && total.screens > 0) {
      numerator += this.data.delivery.screensComplete || 0;
      denominator += total.screens;
    }
    if (this._parent.data.holdHardware && total.hardware > 0) {
      numerator += this.data.delivery.hardwareComplete || 0;
      denominator += total.hardware;
    }
    return { numerator, denominator };
  }

  get progressInstallation() {
    if (
      this.data.install.totalItems > this.data.install.readyItems ||
      this.data.install.totalHours > this.data.install.scheduledHours
    ) {
      return this.percentInstallScheduled;
    } else {
      return this.percentInstalled;
    }
  }

  get percentInstallReady() {
    let label, numerator, denominator;
    if (this.data.install.totalItems > this.data.install.readyItems) {
      label = "Items install ready";
      numerator = this.data.install.readyItems;
      denominator = this.data.install.totalItems;
    } else if (!this.data.install.totalItems || this.data.install.readyItems) {
      label = "Hours Install Ready";
      numerator =
        this.data.install.totalHours - this.data.install.scheduledHours;
      denominator = this.data.install.totalHours;
    }
    const percent = Math.min(100, (100 * numerator) / denominator);
    return {
      label,
      percent,
      numerator,
      denominator,
      reverse: true,
      color: COLORS.warning,
    };
  }

  get percentInstallScheduled() {
    let numerator, denominator;
    numerator = this.data.install.scheduledHours;
    denominator = this.data.install.totalHours;
    const percent = Math.min(100, (100 * numerator) / denominator);
    return { percent, numerator, denominator, color: COLORS.positive };
  }

  get percentInstalled() {
    let label, numerator, denominator;
    if (this.data.install.totalLabors > 1) {
      label = "Installations Complete";
      numerator = this.data.install.completeLabors;
      denominator = this.data.install.totalLabors;
    } else if (this.data.install.totalItems > 0) {
      label = "Items installed";
      numerator = this.data.install.completeItems;
      denominator = this.data.install.totalItems;
    } else {
      label = "Hours Completed";
      numerator = this.data.install.completeHours;
      denominator = this.data.install.totalHours;
    }
    const percent = Math.min(100, (100 * numerator) / denominator);
    return { label, percent, numerator, denominator, color: COLORS.positive };
  }

  get type() {
    return saleStatusType(this.data.type);
  }

  get installReadiness() {
    let type;
    if (this.isFullyDelivered) {
      type = INSTALL_READINESS_TYPES.delivered;
    } else if (this.isFullyReceived && this.isQCInspected) {
      type = INSTALL_READINESS_TYPES.received;
    } else if (this.isFullyAcked) {
      type = INSTALL_READINESS_TYPES.inProduction;
    } else if (this.isFullyOrdered) {
      type = INSTALL_READINESS_TYPES.awaitingAck;
    } else {
      type = INSTALL_READINESS_TYPES.notOrdered;
    }
    return { ...type, ...COLORS[type.color] };
  }

  get expectedInstallDate() {
    // Use ack IDs from sale status instead of filtering all acks
    const ackIDs = Object.keys(this.data.acks);
    if (!ackIDs.length) return null;

    const acks = store.state.main.acks;
    let minDate = null;

    for (const ackID of ackIDs) {
      const ack = acks[ackID];
      if (
        ack &&
        !ack.data.fullyReceived &&
        ack.data.vendor !== "Labor" &&
        ack.data.expectedDate
      ) {
        const date = new Date(ack.data.expectedDate);
        if (!minDate || date < minDate) {
          minDate = date;
        }
      }
    }

    return minDate;
  }

  get installManHours() {
    return this.data.install.totalHours || 0;
  }

  get installSortOrder() {
    const readiness = this.installReadiness;
    const expectedDate = this.expectedInstallDate;
    return readiness.order * 1e14 + (expectedDate ? expectedDate.getTime() : 0);
  }

  get action() {
    let type = this.type;
    return type && type.action ? type.action.bind(this) : null;
  }

  // The sale page tab the status action opens, or null for an action that
  // does something else (a dialog, a prompt)
  get actionSection() {
    return this.type?.action?.section || null;
  }
}
